'use client';

import { useState, useEffect, Suspense } from 'react';
import { signIn, useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import {
  inputClass,
  labelClass,
  formContainerClass,
  errorContainerClass,
  primaryButtonClass,
  formGroupClass,
  fieldContainerClass
} from '@/components/ui/FormStyles';

// Animated background component
const AnimatedBackground = () => {
  useEffect(() => {
    // Create animated shapes
    const createShapes = () => {
      const container = document.getElementById('animated-bg');
      if (!container) return;
      
      // Clear existing shapes
      container.innerHTML = '';
      
      // Create different shapes
      const shapes = ['square', 'circle', 'triangle', 'diamond'];
      
      for (let i = 0; i < 20; i++) {
        const shape = document.createElement('div');
        const size = Math.random() * 80 + 30;
        const posX = Math.random() * 100;
        const posY = Math.random() * 100;
        const duration = Math.random() * 30 + 15;
        const delay = Math.random() * 8;
        const opacity = Math.random() * 0.2 + 0.05;
        const rotation = Math.random() * 360;
        const shapeType = shapes[Math.floor(Math.random() * shapes.length)];
        
        shape.className = `animated-shape ${shapeType}`;
        shape.style.cssText = `
          width: ${size}px;
          height: ${size}px;
          left: ${posX}%;
          top: ${posY}%;
          animation-duration: ${duration}s;
          animation-delay: ${delay}s;
          opacity: ${opacity};
          transform: rotate(${rotation}deg);
          background-color: ${Math.random() > 0.5 ? '#2563eb' : '#4f46e5'};
        `;
        
        container.appendChild(shape);
      }
    };
    
    createShapes();
    window.addEventListener('resize', createShapes);
    
    return () => {
      window.removeEventListener('resize', createShapes);
    };
  }, []);
  
  return (
    <div id="animated-bg" className="fixed inset-0 overflow-hidden pointer-events-none z-0" />
  );
};

// Main component that wraps the signin form with Suspense
export default function SignIn() {
  return (
    <Suspense fallback={
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="inline-block">
              <div className="w-[200px] h-[60px] bg-gray-200 animate-pulse"></div>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Loading...</h2>
        </div>
        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-gray-100 py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          </div>
        </div>
      </div>
    }>
      <SignInContent />
    </Suspense>
  );
}

// Component that uses client-side hooks
function SignInContent() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Redirect to dashboard if already logged in
  useEffect(() => {
    if (session) {
      router.push('/user/dashboard');
    }
  }, [session, router]);

  // Check for registration success and show verification message
  useEffect(() => {
    const registered = searchParams.get('registered');
    if (registered === 'true') {
      setSuccessMessage('Registration successful! Please check your email to verify your account before logging in.');
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const result = await signIn('credentials', {
        redirect: false,
        email,
        password,
        callbackUrl: '/user/dashboard',
        // Set session expiration based on rememberMe
        rememberMe: rememberMe
      });

      if (result?.error) {
        // Check for verification error
        if (result.error.includes('verify your email')) {
          setError('Please verify your email before logging in. Check your inbox for a verification link.');
        } else {
          setError('Invalid email or password');
        }
        setIsLoading(false);
        return;
      }

      // Redirect based on user role (handled by middleware)
      router.push('/user/dashboard');
      router.refresh();
    } catch (error) {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  // Show loading state while checking session
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="inline-block">
              <div className="w-[200px] h-[60px] bg-gray-200 animate-pulse"></div>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative">
      {/* Animated background */}
      <AnimatedBackground />
      
      {/* Animation styles */}
      <style jsx global>{`
        @keyframes float {
          0% {
            transform: translateY(0) translateX(0) rotate(0deg);
          }
          33% {
            transform: translateY(-30px) translateX(30px) rotate(15deg);
          }
          66% {
            transform: translateY(30px) translateX(-30px) rotate(-15deg);
          }
          100% {
            transform: translateY(0) translateX(0) rotate(0deg);
          }
        }
        
        .animated-shape {
          position: absolute;
          filter: blur(20px);
          animation: float infinite ease-in-out;
          z-index: -1;
        }
        
        .circle {
          border-radius: 50%;
        }
        
        .square {
          border-radius: 10%;
        }
        
        .triangle {
          clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
        }
        
        .diamond {
          clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
        }
      `}</style>
      
      <div className="sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="text-center">
          <Link href="/" className="inline-block">
            <Image 
              src="/bdnog.svg" 
              alt="BDNOG Logo" 
              width={200} 
              height={60} 
              priority
            />
          </Link>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">Sign in to your account</h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          Or{' '}
          <Link href="/auth/signup" className="font-medium text-blue-600 hover:text-blue-500">
            create a new account
          </Link>
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className={`${formContainerClass} bg-gray-100`}>
          {successMessage && (
            <div className="mb-6 bg-green-50 border-l-4 border-green-400 p-4 rounded-md shadow-sm">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-green-700 font-medium">{successMessage}</p>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className={errorContainerClass}>
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700 font-medium">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          <form className={formGroupClass} onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email" className={labelClass}>
                Email address
              </label>
              <div className={fieldContainerClass}>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={inputClass}
                  placeholder="Enter your email"
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className={labelClass}>
                Password
              </label>
              <div className={fieldContainerClass}>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass}
                  placeholder="Enter your password"
                />
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm font-medium text-gray-800">
                  Remember me
                </label>
              </div>

              <div className="text-sm">
                <Link href="/auth/forgot-password" className="font-medium text-blue-600 hover:text-blue-500 hover:underline">
                  Forgot your password?
                </Link>
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex justify-center ${primaryButtonClass}`}
              >
                {isLoading ? 'Signing in...' : 'Sign in'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
